<template>
  <div class="flex rounded-sm border border-main bg-surface p-2" :data-testid="`speaker-item-${speakerId}`">
    <img v-if="props.speaker.photoUrl" :src="props.speaker.photoUrl" class="w-[104px]" />
    <div class="ml-4 flex flex-col justify-between">
      <div>
        <p class="mb-1 text-headline-s" data-testid="speaker-item-name">{{ props.speaker.name }}</p>
        <p class="text-body-s text-main">
          {{ props.speaker.title ? `${props.speaker.title} ·` : '' }} {{ props.speaker.partnerCompanyName || '' }}
        </p>
        <p
          v-if="showNoEventsNotification"
          class="inline-flex items-center text-body-s-medium text-error"
          data-testid="speaker-item-no-events"
        >
          <SvgIcon :path="mdiAlertCircle" class="mr-2" :size="22" />{{ t('Speakers.form.errors.noEventsAssigned') }}
        </p>
      </div>
      <div>
        <RouterLink
          v-if="isUpdatable"
          class="mr-4 inline-flex items-center txt-label-s-bold-uppercase"
          data-testid="speaker-item-edit"
          :to="{
            name: 'editSpeaker',
            params: { speakerId },
          }"
        >
          <SvgIcon class="mr-1" :path="mdiPencil" :size="16" />
          {{ t('Speakers.editSpeaker') }}
        </RouterLink>
        <template v-if="getSpeakerConfig['enableEventAssigning']">
          <RouterLink
            v-if="isUpdatable"
            class="mr-4 inline-flex items-center txt-label-s-bold-uppercase"
            data-testid="speaker-item-assign"
            :to="{
              name: 'editSpeakerEvents',
              params: { speakerId },
            }"
          >
            <SvgIcon class="mr-1" :path="mdiLink" :size="16" />
            {{ t('Speakers.assignEvent') }}
          </RouterLink>
          <RouterLink
            v-if="!isUpdatable"
            class="mr-4 inline-flex items-center txt-label-s-bold-uppercase"
            data-testid="speaker-item-view"
            :to="{
              name: 'viewSpeaker',
              params: { speakerId },
            }"
          >
            <SvgIcon class="mr-1" :path="mdiLink" :size="16" />
            {{ t('Speakers.assignEvent') }}
          </RouterLink>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { mdiPencil, mdiLink, mdiAlertCircle } from '@mdi/js'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { Speaker } from '@/gql/myomr'
import { toTypeAndId } from '@/helpers/globalIdHelper'
import { useMeStore } from '@/stores'

import getSpeakerConfig from './getSpeakerConfig'

const { t } = useI18n()
const meStore = useMeStore()

const props = defineProps<{
  speaker: Speaker
}>()

const MIN_NEW_ACCOUNT_TIMESTAMP = new Date().getTime() - 60 * 24 * 60 * 60 * 1000
// day hour min sec msec

const isSpeakerNew = computed(() => {
  return new Date(props.speaker.createdAt).getTime() > MIN_NEW_ACCOUNT_TIMESTAMP
})

const showNoEventsNotification = computed(() => {
  return isUpdatable.value && !props.speaker.eventSpeakers?.length && isSpeakerNew.value
})

const speakerId = computed(() => toTypeAndId(props.speaker.id)['id'])

const isUpdatable = computed(() => {
  if (!meStore.currentPartnerCompanyId || props.speaker.editorialFlag) return false

  const currentPartnerCompanyId = meStore.currentPartnerCompanyId.toString()
  const speakerPartnerCompany = props.speaker.partnerCompany

  if (speakerPartnerCompany) {
    return speakerPartnerCompany.id.endsWith(currentPartnerCompanyId)
  } else {
    return false
  }
})
</script>
